/**
 * Placeholder tự sinh bằng code (không phải asset thật) — phase B dùng charforge.
 *
 * Theo master-plan mục 3.5: "manifest + placeholder tự sinh" — mọi hình ảnh
 * đều được vẽ bằng hàm, không có PNG/JPG thật trong repo nguồn.
 *
 * Hợp đồng: mọi ID trong src/data/manifest.json PHẢI có hàm vẽ trong
 * src/assets/sprites/ (tools/validate-manifest.ts kiểm cả hai chiều).
 *
 * Lúc chạy: nếu dist đã bake atlas (`npm run build` bake sẵn), sprite lấy từ
 * atlas PNG; nếu chưa (dev), vẽ trực tiếp bằng hàm charforge — trong suốt
 * với bên gọi.
 */

import { coVungAtlas, veVungAtlas } from './atlas.js';
import { Raster } from './raster.js';
import { DANG_KY_SPRITE, veSprite } from './sprites/index.js';

export type VeFn = (ctx: CanvasRenderingContext2D, s: number) => void;

interface DinhNghiaPlaceholder {
  /** Màu nền chính (giữ tương thích API cũ). */
  mauNen: string;
  /** Nhãn ngắn (tiếng Việt, giữ tương thích API cũ). */
  nhan: string;
  /** Hàm vẽ chi tiết lên canvas s×s. */
  ve: VeFn;
}

/** Metadata hiển thị cho từng sprite (màu nền + nhãn). */
const THONG_TIN: Record<string, { mauNen: string; nhan: string }> = {
  // 6 kiểu khách.
  'khach-hoc-sinh': { mauNen: '#7fb3e8', nhan: 'Học sinh' },
  'khach-van-phong': { mauNen: '#9aa5b1', nhan: 'Văn phòng' },
  'khach-du-lich': { mauNen: '#f0b35e', nhan: 'Du lịch' },
  'khach-lon-tuoi': { mauNen: '#b8a88e', nhan: 'Lớn tuổi' },
  'khach-dau-bep': { mauNen: '#e8e8e8', nhan: 'Đầu bếp' },
  'khach-dem-khuya': { mauNen: '#4a4a6e', nhan: 'Đêm khuya' },
  // Nội thất.
  'ke-go': { mauNen: '#8a5a2e', nhan: 'Kệ gỗ' },
  'tu-lanh': { mauNen: '#bcd6e8', nhan: 'Tủ lạnh' },
  'quay-thu-ngan': { mauNen: '#a06a3a', nhan: 'Thu ngân' },
  // Môi trường Phố chợ.
  'san-pho-cho': { mauNen: '#c99a5e', nhan: 'Sàn' },
  'via-he': { mauNen: '#9a9a9a', nhan: 'Vỉa hè' },
  'bien-hieu': { mauNen: '#6e3a1a', nhan: 'TẠP HÓA' },
  'cua-ra-vao': { mauNen: '#5e4632', nhan: 'Cửa' },
  'cay-xanh': { mauNen: '#9db87a', nhan: 'Cây xanh' },
  'thung-rac': { mauNen: '#9db87a', nhan: 'Thùng rác' },
  'den-duong': { mauNen: '#8a9ab0', nhan: 'Đèn đường' },
  // Môi trường Bãi biển.
  'san-bai-bien': { mauNen: '#ecd9a0', nhan: 'Cát' },
  'nuoc-bien': { mauNen: '#4f9fd9', nhan: 'Nước biển' },
  'quay-ca-phe': { mauNen: '#6e4a2a', nhan: 'Cà phê' },
  'cay-dua': { mauNen: '#bfe0f5', nhan: 'Dừa' },
  'ghe-bien': { mauNen: '#ecd9a0', nhan: 'Ghế biển' },
  // Môi trường Chợ đêm.
  'san-cho-dem': { mauNen: '#2a2438', nhan: 'Chợ đêm' },
  'den-long': { mauNen: '#2a2438', nhan: 'Đèn lồng' },
  'xe-day': { mauNen: '#2a2438', nhan: 'Xe đẩy' },
  'gian-hang': { mauNen: '#2a2438', nhan: 'Sạp chợ' },
  // 12 NPC.
  'npc-chu-ba': { mauNen: '#e8a84f', nhan: 'Chú Ba' },
  'npc-co-sau': { mauNen: '#c39bd3', nhan: 'Cô Sáu' },
  'npc-anh-tam': { mauNen: '#7fd69b', nhan: 'Anh Tám' },
  'npc-ba-tu': { mauNen: '#f0a0b0', nhan: 'Bà Tư' },
  'npc-anh-hai': { mauNen: '#8fb8e8', nhan: 'Anh Hai' },
  'npc-co-ba': { mauNen: '#7fd6d6', nhan: 'Cô Ba' },
  'npc-chu-sau': { mauNen: '#a0b0c8', nhan: 'Chú Sáu' },
  'npc-em-ut': { mauNen: '#f0d67f', nhan: 'Em Út' },
  'npc-di-nam': { mauNen: '#e8a0a8', nhan: 'Dì Năm' },
  'npc-bac-tu': { mauNen: '#c8b89a', nhan: 'Bác Tư' },
  'npc-chi-hong': { mauNen: '#f0b8d0', nhan: 'Chị Hồng' },
  'npc-ong-chin': { mauNen: '#8a9ab0', nhan: 'Ông Chín' },
  // 6 nhân vật chọn được.
  'nv-hoa': { mauNen: '#f0b35e', nhan: 'Hoa' },
  'nv-minh': { mauNen: '#7fb3e8', nhan: 'Minh' },
  'nv-lam': { mauNen: '#7fd6d6', nhan: 'Lam' },
  'nv-duc': { mauNen: '#9aa5b1', nhan: 'Đức' },
  'nv-thao': { mauNen: '#f0d67f', nhan: 'Thảo' },
  'nv-phuc': { mauNen: '#c8b89a', nhan: 'Phúc' },
  // 2 nhân viên.
  'nhan-vien-thu-ngan': { mauNen: '#8fb8e8', nhan: 'Thu ngân' },
  'nhan-vien-xep-ke': { mauNen: '#8fd6a8', nhan: 'Xếp kệ' },
  // 24 icon món.
  'mon-sp01': { mauNen: '#a8d0f0', nhan: 'Nước suối' },
  'mon-sp02': { mauNen: '#a8d0f0', nhan: 'Nước ngọt' },
  'mon-sp03': { mauNen: '#a8d0f0', nhan: 'Sữa tươi' },
  'mon-sp04': { mauNen: '#a8d0f0', nhan: 'Trà chai' },
  'mon-sp05': { mauNen: '#a8d0f0', nhan: 'Nước dừa' },
  'mon-sp06': { mauNen: '#a8d0f0', nhan: 'Tăng lực' },
  'mon-sp07': { mauNen: '#f0c890', nhan: 'Mì gói' },
  'mon-sp08': { mauNen: '#f0c890', nhan: 'Bánh quy' },
  'mon-sp09': { mauNen: '#f0c890', nhan: 'Gạo' },
  'mon-sp10': { mauNen: '#f0c890', nhan: 'Cá hộp' },
  'mon-sp11': { mauNen: '#f0c890', nhan: 'Kẹo' },
  'mon-sp12': { mauNen: '#f0c890', nhan: 'Bánh tráng' },
  'mon-sp13': { mauNen: '#a8e0a8', nhan: 'Rau cải' },
  'mon-sp14': { mauNen: '#a8e0a8', nhan: 'Trứng gà' },
  'mon-sp15': { mauNen: '#a8e0a8', nhan: 'Thịt heo' },
  'mon-sp16': { mauNen: '#a8e0a8', nhan: 'Cá tươi' },
  'mon-sp17': { mauNen: '#a8e0a8', nhan: 'Đậu hũ' },
  'mon-sp18': { mauNen: '#a8e0a8', nhan: 'Chuối' },
  'mon-sp19': { mauNen: '#d0b8e8', nhan: 'Xà phòng' },
  'mon-sp20': { mauNen: '#d0b8e8', nhan: 'Khăn lau' },
  'mon-sp21': { mauNen: '#d0b8e8', nhan: 'Pin AA' },
  'mon-sp22': { mauNen: '#d0b8e8', nhan: 'KĐR' },
  'mon-sp23': { mauNen: '#d0b8e8', nhan: 'Dầu gội' },
  'mon-sp24': { mauNen: '#d0b8e8', nhan: 'Túi nilon' },
};

/** Vẽ một sprite charforge lên context (atlas trước, hàm vẽ sau). */
function veSpriteLenCanvas(ctx: CanvasRenderingContext2D, id: string, s: number): void {
  if (veVungAtlas(ctx, id, s)) return;
  const r = new Raster(48, 48);
  veSprite(id, r);
  const tam = document.createElement('canvas');
  tam.width = 48;
  tam.height = 48;
  const tctx = tam.getContext('2d');
  if (!tctx) {
    throw new Error('Không tạo được context 2D cho sprite.');
  }
  tctx.putImageData(new ImageData(r.sangRGBA(), 48, 48), 0, 0);
  ctx.imageSmoothingEnabled = false; // Giữ nét pixel khi phóng.
  ctx.drawImage(tam, 0, 0, s, s);
}

/** Registry: ID manifest → định nghĩa vẽ. PHẢI khớp 1–1 với manifest.json. */
export const DANH_SACH_PLACEHOLDER: Record<string, DinhNghiaPlaceholder> = (() => {
  const bang: Record<string, DinhNghiaPlaceholder> = {};
  for (const id of Object.keys(DANG_KY_SPRITE)) {
    const thongTin = THONG_TIN[id];
    if (!thongTin) {
      throw new Error(`Sprite "${id}" thiếu metadata (mauNen/nhan) trong placeholder.ts.`);
    }
    bang[id] = {
      mauNen: thongTin.mauNen,
      nhan: thongTin.nhan,
      ve: (ctx, s) => veSpriteLenCanvas(ctx, id, s),
    };
  }
  return bang;
})();

/** Vẽ placeholder theo ID manifest lên canvas s×s, trả về canvas. */
export function veCanvasPlaceholder(id: string, s = 48): HTMLCanvasElement {
  const dinhNghia = DANH_SACH_PLACEHOLDER[id];
  if (!dinhNghia) {
    throw new Error(`Không có placeholder cho asset "${id}" — kiểm tra manifest.`);
  }
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Không tạo được context 2D cho placeholder.');
  }
  ctx.fillStyle = dinhNghia.mauNen;
  ctx.fillRect(0, 0, s, s);
  dinhNghia.ve(ctx, s);
  return canvas;
}

/** Vẽ placeholder theo ID manifest, trả về data URL PNG (sinh lúc chạy). */
export function vePlaceholder(id: string, s = 48): string {
  return veCanvasPlaceholder(id, s).toDataURL('image/png');
}

/**
 * Vẽ một ô 48×48 có nhãn (giữ tương thích P0).
 */
export function vePlaceholder48(nhan: string, mauNen = '#3a6ea5'): string {
  const canvas = document.createElement('canvas');
  canvas.width = 48;
  canvas.height = 48;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Không tạo được context 2D cho placeholder.');
  }
  ctx.fillStyle = mauNen;
  ctx.fillRect(0, 0, 48, 48);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, 44, 44);
  ctx.fillStyle = '#ffffff';
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(nhan.slice(0, 6), 24, 24);
  return canvas.toDataURL('image/png');
}

/** true nếu sprite này đang được phục vụ từ atlas đã bake. */
export function dungAtlas(id: string): boolean {
  return coVungAtlas(id);
}
